<script>
    import BrandImage from "@xd/components/BrandImage";
    import BrandLink from "@xd/components/BrandLink";

    export default {
        name: "BrandProductUrban",
        components: { BrandLink, BrandImage },
        props: {
            product: Object,
            visibleElements: Array,
        },
        methods: {
            checkElement(index) {
                return !this.visibleElements.includes(index.toString());
            },
        },
    };
</script>

<template>
    <div class="brand-product" :id="product.slug">
        <div class="l-container">
            <div
                class="brand-product__blur"
                :class="{ 'is-animated': checkElement('blur') }"
                data-name="blur"
            >
                <img src="/static/xd-design/brand-history/blur.svg" alt="" />
            </div>

            <svg
                class="brand-product__line is-mobile"
                :class="{ 'is-animated': checkElement('line-mobile') }"
                data-name="line-mobile"
                width="591"
                height="630"
                viewBox="0 0 591 630"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M590.146 532.73.683 629.106 436.823 1l153.323 531.73Z"
                    stroke="#182028"
                    stroke-width=".5"
                />
            </svg>

            <svg
                class="brand-product__line is-desktop"
                :class="{ 'is-animated': checkElement('line-desktop') }"
                data-name="line-desktop"
                width="696"
                height="742"
                viewBox="0 0 696 742"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M695.226 627.729 1.558 741.143 514.8 2l180.427 625.729Z"
                    stroke="#182028"
                />
            </svg>

            <header class="brand-product__header">
                <div
                    class="brand-product__title"
                    :class="{ 'is-animated': checkElement('title') }"
                    data-name="title"
                >
                    {{ product.title }}
                </div>
                <div
                    class="brand-product__date"
                    :class="{ 'is-animated': checkElement('date') }"
                    data-name="date"
                >
                    {{ product.date }}
                </div>
            </header>

            <div class="brand-product__body">
                <div class="brand-product__col">
                    <div class="brand-product__image image-1">
                        <brand-image
                            :image="product.image"
                            index="1"
                            :is-animated="checkElement('image-1')"
                            data-name="image-1"
                        ></brand-image>
                    </div>

                    <div
                        class="brand-product__text text-1"
                        :class="{ 'is-animated': checkElement('text-1') }"
                        data-name="text-1"
                        v-html="product.text[0]"
                    ></div>
                </div>

                <div class="brand-product__col">
                    <div class="brand-product__image image-2">
                        <brand-image
                            :image="product.image"
                            index="2"
                            :is-animated="checkElement('image-2')"
                            data-name="image-2"
                        ></brand-image>
                    </div>

                    <div
                        class="brand-product__text text-2"
                        :class="{ 'is-animated': checkElement('text-2') }"
                        data-name="text-2"
                        v-html="product.text[1]"
                    ></div>

                    <div
                        class="brand-product__text text-3"
                        :class="{ 'is-animated': checkElement('text-3') }"
                        data-name="text-3"
                        v-html="product.text[2]"
                    ></div>

                    <div class="brand-product__image image-3">
                        <brand-image
                            :image="product.image"
                            index="3"
                            :is-animated="checkElement('image-2')"
                            data-name="image-2"
                        ></brand-image>
                    </div>

                    <brand-link
                        class="brand-product__link"
                        :slug="product.slug"
                        :is-animated="checkElement('link')"
                        data-name="link"
                    ></brand-link>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .brand-product {
        padding: 110px 16px 10px;
        @include bp($bp-desktop-sm) {
            padding: 100px 0 150px;
        }
    }

    .brand-product__header {
        margin-bottom: 40px;
        @include bp($bp-desktop-lg) {
            position: relative;
            margin-bottom: 115px;
        }
    }

    .brand-product__date {
        @include bp($bp-desktop-lg) {
            position: absolute;
            left: 605px;
            bottom: 0;
        }
    }

    .brand-product__body {
        @include bp($bp-desktop-lg) {
            display: flex;
            flex-direction: row-reverse;
            margin: 0 -40px;
        }
    }

    .brand-product__col {
        @include bp($bp-desktop-lg) {
            display: flex;
            flex-grow: 1;
            margin: 0 40px;
            &:first-child {
                flex-direction: row-reverse;
                justify-content: space-between;
                align-items: flex-start;
            }
            &:last-child {
                flex-direction: column;
                flex-basis: 524px;
                max-width: 524px;
            }
        }
    }

    .brand-product__image {
        &.image-1 {
            width: calc(100% + 32px);
            margin-right: -32px;
            margin-bottom: 40px;
            &::before {
                padding-bottom: 161.8%;
            }
            @include bp($bp-desktop-lg) {
                width: 476px;
                margin-right: -90px;
                margin-top: -165px;
                margin-bottom: 0;
            }
        }

        &.image-2 {
            width: calc(100% + 32px);
            margin-left: -32px;
            margin-bottom: 40px;
            &::before {
                padding-bottom: 56.4%;
            }
            @include bp($bp-desktop-lg) {
                width: 614px;
                margin-left: -90px;
                margin-bottom: 32px;
            }
        }

        &.image-3 {
            width: calc(100% + 32px);
            margin-right: -32px;
            &::before {
                padding-bottom: 95.6%;
            }
            @include bp($bp-desktop-lg) {
                max-width: 524px;
                margin-right: 0;
            }
        }
    }

    .brand-product__text {
        &.text-1 {
            max-width: 245px;
            margin-bottom: 40px;
            @include bp($bp-desktop-lg) {
                max-width: 180px;
            }
        }

        &.text-2 {
            margin-bottom: 16px;
            @include bp($bp-desktop-lg) {
                margin-bottom: 24px;
            }
        }

        &.text-3 {
            margin-bottom: 40px;
            @include bp($bp-desktop-lg) {
                margin-bottom: 55px;
            }
        }
    }

    .brand-product__link {
        margin-top: 40px;
        @include bp($bp-desktop-lg) {
            align-self: flex-end;
            margin-top: -50px;
            margin-right: -380px;
        }
    }

    .brand-product__line {
        top: 260px;
        right: 0;
        stroke-dashoffset: 0;
        stroke-dasharray: 1916;
        transition: all 3s ease-in;
        animation-duration: 4.2s;
        animation-delay: 3.2s;
        &.is-animated {
            stroke-dashoffset: 1916;
        }
        @include bp($bp-mobile-s) {
            top: 360px;
        }
        @include bp($bp-desktop-lg) {
            top: 140px;
            right: 130px;
            stroke-dasharray: 2254;
            &.is-animated {
                stroke-dashoffset: 2254;
            }
        }
    }

    .brand-product__blur {
        top: 750px;
        right: -211px;
        @include box(423px);
        @include bp($bp-desktop-lg) {
            top: 460px;
            right: -106px;
            @include box(472px);
        }
    }
</style>
